// Page de gestion des interventions - Version corrigée avec modal photos
// Fichier: /var/www/squarecom/frontend/src/pages/Interventions.js

import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Alert,
  Tooltip,
  Badge,
  Card,
  CardContent,
  Stack,
  CircularProgress,
  Tab,
  Tabs,
  FormControlLabel,
  Switch,
  Divider
} from '@mui/material';
import {
  Visibility,
  Edit,
  Delete,
  Refresh,
  Add,
  LocationOn,
  Close,
  Assignment,
  CheckCircle,
  Schedule,
  Build,
  Warning,
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material';

function Interventions() {
  const [interventions, setInterventions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(0);
  const [viewDialog, setViewDialog] = useState({ open: false, intervention: null });
  const [editDialog, setEditDialog] = useState({ open: false, intervention: null });
  const [assignDialog, setAssignDialog] = useState({ open: false, intervention: null });
  const [closeDialog, setCloseDialog] = useState({ open: false, intervention: null });
  const [error, setError] = useState(null);
  const [technicians, setTechnicians] = useState([]);
  
  // Nouvel état pour la modal d'images
  const [imageModal, setImageModal] = useState({ 
    open: false, 
    url: '', 
    index: 0, 
    photos: [] 
  });

  const [assignmentForm, setAssignmentForm] = useState({
    technicianId: '',
    scheduledDate: '',
    scheduledTime: '',
    estimatedDuration: 2,
    estimatedCost: 0,
    instructions: '',
    sendNotification: true
  });

  const [closeForm, setCloseForm] = useState({
    resolutionNote: '',
    actualDuration: 1,
    actualCost: 0,
    notifyRequester: true
  });

  const todayStr = new Date().toISOString().split('T')[0];

  useEffect(() => {
    loadInterventions();
    const interval = setInterval(loadInterventions, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadInterventions = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      if (!token) {
        setError('Non authentifié');
        setLoading(false);
        return;
      }

      const response = await fetch('/api/interventions/all', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}`);
      }

      const data = await response.json();
      setInterventions(data.interventions || []);

    } catch (error) {
      console.error('Erreur chargement:', error);
      setError('Erreur de chargement des interventions');
    } finally {
      setLoading(false);
    }
  };

  const loadTechnicians = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/users/technicians', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setTechnicians(Array.isArray(data.technicians) ? data.technicians : []);
    } catch (error) {
      console.error('Erreur chargement techniciens:', error);
    }
  };

  // Nouvelles fonctions pour gérer la modal d'images
  const handleImageClick = (photoUrl, index, allPhotos) => {
    setImageModal({
      open: true,
      url: photoUrl,
      index: index,
      photos: allPhotos
    });
  };

  const handleImageModalClose = () => {
    setImageModal({ open: false, url: '', index: 0, photos: [] });
  };

  const handlePreviousImage = () => {
    const newIndex = imageModal.index > 0 ? imageModal.index - 1 : imageModal.photos.length - 1;
    const newPhoto = imageModal.photos[newIndex];
    const newUrl = typeof newPhoto === 'string' ? newPhoto : (newPhoto.url || newPhoto.path);
    setImageModal({ ...imageModal, index: newIndex, url: newUrl });
  };

  const handleNextImage = () => {
    const newIndex = imageModal.index < imageModal.photos.length - 1 ? imageModal.index + 1 : 0;
    const newPhoto = imageModal.photos[newIndex];
    const newUrl = typeof newPhoto === 'string' ? newPhoto : (newPhoto.url || newPhoto.path);
    setImageModal({ ...imageModal, index: newIndex, url: newUrl });
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'urgent': 'error',
      'high': 'warning',
      'medium': 'info',
      'low': 'default'
    };
    return colors[priority] || 'default';
  };

  const getPriorityLabel = (priority) => {
    const labels = {
      'urgent': 'Urgent',
      'high': 'Haute',
      'medium': 'Moyenne',
      'low': 'Faible'
    };
    return labels[priority] || priority || 'N/A';
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'warning',
      'assigned': 'info',
      'in_progress': 'primary',
      'completed': 'success',
      'cancelled': 'default'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'En attente',
      'assigned': 'Assignée',
      'in_progress': 'En cours',
      'completed': 'Terminée',
      'cancelled': 'Annulée'
    };
    return labels[status] || status || 'N/A';
  };

  const getCategoryIcon = (category) => {
    if (!category) return '🔧';
    const cat = category.toLowerCase();
    if (cat.includes('plomb')) return '🚰';
    if (cat.includes('électr') || cat.includes('éclairage')) return '💡';
    if (cat.includes('ascenseur')) return '🛗';
    if (cat.includes('chauff')) return '🔥';
    if (cat.includes('nettoy') || cat.includes('propreté')) return '🧹';
    if (cat.includes('sécur')) return '🔒';
    return '🔧';
  };

  const handleAssignClick = (intervention) => {
    setAssignDialog({ open: true, intervention });
    loadTechnicians();
  };

  const handleAssignSubmit = async () => {
    try {
      const token = localStorage.getItem('token');
      const scheduledDateTime = assignmentForm.scheduledDate && assignmentForm.scheduledTime
        ? `${assignmentForm.scheduledDate}T${assignmentForm.scheduledTime}:00`
        : null;

      await fetch(`/api/interventions/${assignDialog.intervention.id}/assign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          technicianId: assignmentForm.technicianId,
          scheduledDate: scheduledDateTime,
          estimatedDuration: assignmentForm.estimatedDuration,
          estimatedCost: assignmentForm.estimatedCost,
          instructions: assignmentForm.instructions,
          sendNotification: assignmentForm.sendNotification
        })
      });

      setAssignDialog({ open: false, intervention: null });
      setAssignmentForm({
        technicianId: '',
        scheduledDate: '',
        scheduledTime: '',
        estimatedDuration: 2,
        estimatedCost: 0,
        instructions: '',
        sendNotification: true
      });
      loadInterventions();
    } catch (error) {
      console.error('Erreur attribution:', error);
    }
  };

  const handleCloseIntervention = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`/api/interventions/${closeDialog.intervention.id}/close`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(closeForm)
      });

      setCloseDialog({ open: false, intervention: null });
      setCloseForm({
        resolutionNote: '',
        actualDuration: 1,
        actualCost: 0,
        notifyRequester: true
      });
      loadInterventions();
    } catch (error) {
      console.error('Erreur clôture:', error);
    }
  };

  const handleViewIntervention = (intervention) => {
    setViewDialog({ open: true, intervention });
  };

  const handleEditIntervention = (intervention) => {
    setEditDialog({ open: true, intervention: { ...intervention } });
  };

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/interventions/${editDialog.intervention.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: editDialog.intervention.title,
          status: editDialog.intervention.status,
          priority: editDialog.intervention.priority
        })
      });

      if (response.ok) {
        await loadInterventions();
        setEditDialog({ open: false, intervention: null });
      }
    } catch (error) {
      console.error('Erreur modification:', error);
    }
  };

  const handleDeleteIntervention = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette intervention ?')) {
      try {
        const token = localStorage.getItem('token');
        await fetch(`/api/interventions/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        await loadInterventions();
      } catch (error) {
        console.error('Erreur suppression:', error);
      }
    }
  };

  const getFilteredInterventions = () => {
    if (!Array.isArray(interventions)) return [];

    switch(selectedTab) {
      case 0: return interventions;
      case 1: return interventions.filter(i => i.priority === 'urgent' || i.priority === 'high');
      case 2: return interventions.filter(i => i.status === 'pending');
      case 3: return interventions.filter(i => i.status === 'in_progress' || i.status === 'assigned');
      case 4: return interventions.filter(i => i.status === 'completed');
      default: return interventions;
    }
  };

  const filteredInterventions = getFilteredInterventions();

  const counts = {
    total: interventions.length,
    urgent: interventions.filter(i => i.priority === 'urgent' || i.priority === 'high').length,
    pending: interventions.filter(i => i.status === 'pending').length,
    assigned: interventions.filter(i => i.status === 'assigned').length,
    inProgress: interventions.filter(i => i.status === 'in_progress').length,
    completed: interventions.filter(i => i.status === 'completed').length
  };

  if (loading && interventions.length === 0) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      {/* ✅ MODIFICATION APPLIQUÉE : Container maxWidth="xl" au lieu de "lg" */}
      <Alert severity="success" sx={{ mb: 2, bgcolor: '#4CAF50', color: 'white', fontWeight: 'bold' }}>
        ✅ VERSION MODIFIÉE - Container maxWidth="xl" (largeur identique au Dashboard)
      </Alert>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Gestion des Interventions
        </Typography>
        <Box>
          <IconButton onClick={loadInterventions}>
            <Refresh />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<Add />}
            href="/scan/new"
          >
            Nouvelle
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">Total</Typography>
              <Typography variant="h4">{counts.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ bgcolor: 'error.light' }}>
            <CardContent>
              <Typography color="white" gutterBottom variant="body2">Urgentes</Typography>
              <Typography variant="h4" color="white">{counts.urgent}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ bgcolor: 'warning.light' }}>
            <CardContent>
              <Typography color="white" gutterBottom variant="body2">En attente</Typography>
              <Typography variant="h4" color="white">{counts.pending}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ bgcolor: 'info.light' }}>
            <CardContent>
              <Typography color="white" gutterBottom variant="body2">Assignées</Typography>
              <Typography variant="h4" color="white">{counts.assigned}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ bgcolor: 'primary.light' }}>
            <CardContent>
              <Typography color="white" gutterBottom variant="body2">En cours</Typography>
              <Typography variant="h4" color="white">{counts.inProgress}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ bgcolor: 'success.light' }}>
            <CardContent>
              <Typography color="white" gutterBottom variant="body2">Terminées</Typography>
              <Typography variant="h4" color="white">{counts.completed}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={selectedTab} onChange={(e, v) => setSelectedTab(v)}>
          <Tab label={`TOUTES (${counts.total})`} />
          <Tab label={`URGENTES (${counts.urgent})`} />
          <Tab label={`EN ATTENTE (${counts.pending})`} />
          <Tab label={`EN COURS (${counts.assigned + counts.inProgress})`} />
          <Tab label={`TERMINÉES (${counts.completed})`} />
        </Tabs>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Titre</TableCell>
              <TableCell>Catégorie</TableCell>
              <TableCell>Emplacement</TableCell>
              <TableCell>Priorité</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredInterventions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="textSecondary">
                    Aucune intervention trouvée
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredInterventions.map((intervention, index) => (
                <TableRow key={intervention.id || index}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{getCategoryIcon(intervention.category)}</span>
                      <Typography variant="body2" fontWeight="medium">
                        {intervention.title || 'Sans titre'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{intervention.category || 'N/A'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LocationOn fontSize="small" color="action" />
                      {intervention.location || 'N/A'}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getPriorityLabel(intervention.priority)}
                      color={getPriorityColor(intervention.priority)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(intervention.status)}
                      color={getStatusColor(intervention.status)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {intervention.createdAt ?
                      new Date(intervention.createdAt).toLocaleDateString('fr-FR') :
                      'N/A'
                    }
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Voir">
                        <IconButton size="small" onClick={() => handleViewIntervention(intervention)}>
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      {intervention.status === 'pending' && (
                        <Tooltip title="Attribuer">
                          <IconButton size="small" color="primary" onClick={() => handleAssignClick(intervention)}>
                            <Assignment />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Modifier">
                        <IconButton size="small" onClick={() => handleEditIntervention(intervention)}>
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      {(intervention.status === 'assigned' || intervention.status === 'in_progress') ? (
                        <Tooltip title="Clôturer">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => setCloseDialog({ open: true, intervention })}
                          >
                            <CheckCircle />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Supprimer">
                          <IconButton size="small" color="error" onClick={() => handleDeleteIntervention(intervention.id)}>
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog d'attribution */}
      <Dialog open={assignDialog.open} onClose={() => setAssignDialog({ open: false, intervention: null })} maxWidth="md" fullWidth>
        <DialogTitle>
          Attribuer l'intervention
          {assignDialog.intervention && (
            <>
              <Typography variant="subtitle2" color="textSecondary">
                {assignDialog.intervention.title}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                <LocationOn fontSize="small" sx={{ verticalAlign: 'middle' }} />
                {assignDialog.intervention.residenceName || assignDialog.intervention.location}
              </Typography>
            </>
          )}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Sélectionner un technicien</InputLabel>
                <Select
                  value={assignmentForm.technicianId}
                  onChange={(e) => setAssignmentForm({...assignmentForm, technicianId: e.target.value})}
                  label="Sélectionner un technicien"
                >
                  {technicians.map(t => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.firstName} {t.lastName} - {t.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                type="date"
                label="Date d'intervention"
                value={assignmentForm.scheduledDate}
                onChange={(e) => setAssignmentForm({...assignmentForm, scheduledDate: e.target.value})}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: todayStr }}
                placeholder="jj/mm/aaaa"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="time"
                label="Heure"
                value={assignmentForm.scheduledTime}
                onChange={(e) => setAssignmentForm({...assignmentForm, scheduledTime: e.target.value})}
                InputLabelProps={{ shrink: true }}
                placeholder="--:--"
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Durée estimée (heures)"
                value={assignmentForm.estimatedDuration}
                onChange={(e) => setAssignmentForm({...assignmentForm, estimatedDuration: e.target.value})}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Coût estimé (€)"
                value={assignmentForm.estimatedCost}
                onChange={(e) => setAssignmentForm({...assignmentForm, estimatedCost: e.target.value})}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Instructions pour le technicien"
                value={assignmentForm.instructions}
                onChange={(e) => setAssignmentForm({...assignmentForm, instructions: e.target.value})}
                placeholder="Instructions spécifiques pour cette intervention..."
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={assignmentForm.sendNotification}
                    onChange={(e) => setAssignmentForm({...assignmentForm, sendNotification: e.target.checked})}
                    color="primary"
                  />
                }
                label="Envoyer une notification au technicien"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialog({ open: false, intervention: null })}>
            ANNULER
          </Button>
          <Button
            onClick={handleAssignSubmit}
            variant="contained"
            disabled={!assignmentForm.technicianId}
          >
            ATTRIBUER
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de clôture */}
      <Dialog open={closeDialog.open} onClose={() => setCloseDialog({ open: false, intervention: null })} maxWidth="sm" fullWidth>
        <DialogTitle>
          Clôturer l'intervention
          {closeDialog.intervention && (
            <Typography variant="subtitle2" color="textSecondary">
              {closeDialog.intervention.title}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Note de résolution"
                value={closeForm.resolutionNote}
                onChange={(e) => setCloseForm({...closeForm, resolutionNote: e.target.value})}
                placeholder="Décrivez les travaux effectués..."
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Durée réelle (heures)"
                value={closeForm.actualDuration}
                onChange={(e) => setCloseForm({...closeForm, actualDuration: e.target.value})}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Coût réel (€)"
                value={closeForm.actualCost}
                onChange={(e) => setCloseForm({...closeForm, actualCost: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={closeForm.notifyRequester}
                    onChange={(e) => setCloseForm({...closeForm, notifyRequester: e.target.checked})}
                    color="primary"
                  />
                }
                label="Notifier le demandeur"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseDialog({ open: false, intervention: null })}>
            Annuler
          </Button>
          <Button
            onClick={handleCloseIntervention}
            variant="contained"
            color="success"
            disabled={!closeForm.resolutionNote}
          >
            Clôturer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de visualisation avec photos */}
      <Dialog open={viewDialog.open} onClose={() => setViewDialog({ open: false, intervention: null })} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Détails de l'intervention</Typography>
            <IconButton onClick={() => setViewDialog({ open: false, intervention: null })}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {viewDialog.intervention && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Alert severity={getPriorityColor(viewDialog.intervention.priority)}>
                  <Typography variant="h6">{viewDialog.intervention.title || 'Sans titre'}</Typography>
                </Alert>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Catégorie</Typography>
                <Typography>{viewDialog.intervention.category || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Priorité</Typography>
                <Typography>{getPriorityLabel(viewDialog.intervention.priority)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Statut</Typography>
                <Chip
                  label={getStatusLabel(viewDialog.intervention.status)}
                  color={getStatusColor(viewDialog.intervention.status)}
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Emplacement</Typography>
                <Typography>{viewDialog.intervention.location || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">Description</Typography>
                <Typography>{viewDialog.intervention.description || 'Aucune description'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Résidence</Typography>
                <Typography>{viewDialog.intervention.residenceName || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Date de création</Typography>
                <Typography>
                  {viewDialog.intervention.createdAt ?
                    new Date(viewDialog.intervention.createdAt).toLocaleString('fr-FR') :
                    'N/A'
                  }
                </Typography>
              </Grid>

              {/* Affichage des photos - VERSION CORRIGÉE */}
              {(() => {
                try {
                  if (!viewDialog.intervention.photos) return null;

                  const photos = typeof viewDialog.intervention.photos === 'string'
                    ? JSON.parse(viewDialog.intervention.photos)
                    : viewDialog.intervention.photos;

                  if (!Array.isArray(photos) || photos.length === 0) return null;

                  return (
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                        Photos ({photos.length})
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                        {photos.map((photo, index) => {
                          const photoUrl = typeof photo === 'string' ? photo : (photo.url || photo.path);
                          if (!photoUrl) return null;

                          return (
                            <Box
                              key={index}
                              sx={{
                                position: 'relative',
                                width: 200,
                                height: 200,
                                cursor: 'pointer',
                                borderRadius: 2,
                                overflow: 'hidden',
                                boxShadow: 2,
                                '&:hover': {
                                  boxShadow: 4,
                                  transform: 'scale(1.02)',
                                  transition: 'all 0.2s'
                                }
                              }}
                              onClick={() => handleImageClick(photoUrl, index, photos)}
                            >
                              <img
                                src={photoUrl}
                                alt={`Photo ${index + 1}`}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                                onError={(e) => {
                                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect width="200" height="200" fill="%23f0f0f0"/%3E%3Ctext x="50%25" y="50%25" font-size="14" fill="%23999" text-anchor="middle" dominant-baseline="middle"%3EImage non disponible%3C/text%3E%3C/svg%3E';
                                }}
                              />
                              {photo.caption && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    bgcolor: 'rgba(0,0,0,0.7)',
                                    color: 'white',
                                    p: 1
                                  }}
                                >
                                  {photo.caption}
                                </Typography>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                      <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                        Cliquez sur une photo pour l'agrandir
                      </Typography>
                    </Grid>
                  );
                } catch (error) {
                  console.error('Erreur affichage photos:', error);
                  return null;
                }
              })()}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog({ open: false, intervention: null })}>
            Fermer
          </Button>
          {viewDialog.intervention?.status === 'pending' && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setViewDialog({ open: false, intervention: null });
                handleAssignClick(viewDialog.intervention);
              }}
            >
              Attribuer
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* NOUVELLE MODAL POUR AFFICHER LES IMAGES EN GRAND */}
      <Dialog
        open={imageModal.open}
        onClose={handleImageModalClose}
        maxWidth={false}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            boxShadow: 'none',
            margin: 2,
            maxHeight: '95vh',
            maxWidth: '95vw',
          }
        }}
      >
        <DialogTitle sx={{ 
          color: 'white', 
          pb: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography>
            Photo {imageModal.index + 1} sur {imageModal.photos.length}
          </Typography>
          <IconButton onClick={handleImageModalClose} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          position: 'relative',
          p: 2
        }}>
          {imageModal.photos.length > 1 && (
            <>
              <IconButton
                onClick={handlePreviousImage}
                sx={{
                  position: 'absolute',
                  left: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  },
                  zIndex: 1
                }}
              >
                <ChevronLeft />
              </IconButton>
              <IconButton
                onClick={handleNextImage}
                sx={{
                  position: 'absolute',
                  right: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  },
                  zIndex: 1
                }}
              >
                <ChevronRight />
              </IconButton>
            </>
          )}
          <Box
            component="img"
            src={imageModal.url}
            alt={`Photo ${imageModal.index + 1}`}
            sx={{
              maxWidth: '100%',
              maxHeight: '80vh',
              objectFit: 'contain',
              borderRadius: 1,
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Dialog de modification */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, intervention: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Modifier l'intervention</DialogTitle>
        <DialogContent>
          {editDialog.intervention && (
            <Stack spacing={2} sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Titre"
                value={editDialog.intervention.title || ''}
                onChange={(e) => setEditDialog({
                  ...editDialog,
                  intervention: { ...editDialog.intervention, title: e.target.value }
                })}
              />
              <FormControl fullWidth>
                <InputLabel>Statut</InputLabel>
                <Select
                  value={editDialog.intervention.status || 'pending'}
                  onChange={(e) => setEditDialog({
                    ...editDialog,
                    intervention: { ...editDialog.intervention, status: e.target.value }
                  })}
                  label="Statut"
                >
                  <MenuItem value="pending">En attente</MenuItem>
                  <MenuItem value="assigned">Assignée</MenuItem>
                  <MenuItem value="in_progress">En cours</MenuItem>
                  <MenuItem value="completed">Terminée</MenuItem>
                  <MenuItem value="cancelled">Annulée</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Priorité</InputLabel>
                <Select
                  value={editDialog.intervention.priority || 'medium'}
                  onChange={(e) => setEditDialog({
                    ...editDialog,
                    intervention: { ...editDialog.intervention, priority: e.target.value }
                  })}
                  label="Priorité"
                >
                  <MenuItem value="low">Faible</MenuItem>
                  <MenuItem value="medium">Moyenne</MenuItem>
                  <MenuItem value="high">Haute</MenuItem>
                  <MenuItem value="urgent">Urgente</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, intervention: null })}>
            Annuler
          </Button>
          <Button onClick={handleSaveEdit} variant="contained">
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Interventions;